import React, { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import _get from "lodash/get";
import _cloneDeep from "lodash/cloneDeep";
import _set from "lodash/set";
import * as Preview from "@repoflow/components/preview";
import { useEditor } from "@repoflow/components/editor";
import { previewSrc } from "env";
import * as SiteQL from "queries/site";

import { previewPage, sectionPath } from "../preview";

// The right-hand pane of the section editor: the section as it would render,
// live.
//
// The same protocol, the same frame and the same composer as the page editor's
// pane — see ../../../../pages/page/edit/frame, which this is deliberately a
// thin copy of. What differs is only what goes in: one section rather than a
// page, so there is nothing to fetch sections FOR, nothing to select, and the
// only element there is is the one ../preview builds.
export const Frame = ({ siteId, state }) => {
  const client = useApolloClient();
  const { auto, setAuto } = useEditor();

  const { html, style, script, types, values, frame } = state.values;

  // The composer's shape, and the value the frame watches. Memoised on the
  // fields that reach the preview — so typing in one of them reloads the frame,
  // and editing `tags`, which is a label rather than content, does not.
  const page = useMemo(
    () => previewPage({ html, style, script, types, values, frame }),
    [html, style, script, types, values, frame]
  );

  // Compose the payload. Called each time the frame reloads and announces
  // itself.
  const onMounted = useCallback(
    async ({ send, setLoading, position }) => {
      setLoading(true);

      // The site, for the values and attributes its configuration declares. A
      // section is where `{{site.values.brand}}` is actually written, so this
      // pane is the one that has to resolve it — and it has to resolve it the
      // same way the page editor's pane does, or a section that looks right
      // here is wrong on every page that carries it.
      //
      // Cache-first, the default: the site route above this screen has already
      // read it.
      const site = await client.query({
        query: SiteQL.get,
        variables: { siteId },
      });

      setLoading(false);

      send("mount", {
        page,
        // A section cannot reference another section — only a page element can
        // — so there is nothing to resolve and no round trip to make.
        sections: {},
        site: _get(site, "data.viewer.account.sites.get"),
        // A page carries editor parameters for the templates that read
        // `{{params.x}}`; a section has no field for them. A section that reads
        // one previews it as empty here, which is what a page would show for a
        // parameter nobody set.
        params: {},
        // Nothing to select: there is one element and it is the section.
        selected: null,
        position,
      });
    },
    [client, siteId, page]
  );

  // Somebody typed into a `data-edit` node in the preview.
  //
  // cloneDeep before set, because lodash's `set` mutates: writing through the
  // form's current values would change them in place, so `isEqual` would find
  // nothing to save and the Save button would never light up.
  const onInput = useCallback(
    ({ datapath, value }) => {
      const path = sectionPath(datapath);

      if (!path) {
        return;
      }

      const mod = _cloneDeep(state.values);
      _set(mod, path, value);
      state.setValues(mod);
    },
    [state]
  );

  return (
    <Preview.Frame
      // Absolute, and since P142 usually CROSS-ORIGIN: the frame is the same
      // preview route served from the sites hostname, so a tenant's `<script>`
      // does not run with this application's origin. `previewSrc` falls back to
      // this origin where there is no distribution name to build one from.
      src={previewSrc()}
      watch={page}
      selected={null}
      auto={auto}
      onAutoChange={setAuto}
      // The dimmer highlights the SELECTED element against the others, and here
      // there is one element and no way to select it. Its toggle is hidden
      // rather than shown doing nothing.
      highlight={false}
      hideHighlight
      onMounted={onMounted}
      onInput={onInput}
    />
  );
};
